import React, { Component } from "react";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";

// Type declaration for Promise.withResolvers (ES2024 feature)
declare global {
  interface PromiseConstructor {
    withResolvers<T>(): {
      promise: Promise<T>;
      resolve: (value: T | PromiseLike<T>) => void;
      reject: (reason?: any) => void;
    };
  }
}

// Polyfill for Promise.withResolvers (ES2024 feature)
// Required for PDF.js 4.4.168 in older browsers/environments
if (typeof Promise.withResolvers === "undefined") {
  Promise.withResolvers = function <T>() {
    let resolve: (value: T | PromiseLike<T>) => void;
    let reject: (reason?: any) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve: resolve!, reject: reject! };
  };
}

interface Props {
  workerSrc?: string;
  url: string;
  beforeLoad: JSX.Element;
  errorMessage?: JSX.Element;
  children: (pdfDocument: PDFDocumentProxy) => JSX.Element;
  onError?: (error: Error) => void;
  cMapUrl?: string;
  cMapPacked?: boolean;
  httpHeaders?: Record<string, string>;
}

interface State {
  pdfDocument: PDFDocumentProxy | null;
  error: Error | null;
}

export class PdfLoader extends Component<Props, State> {
  state: State = {
    pdfDocument: null,
    error: null,
  };

  static defaultProps = {
    workerSrc: undefined,
  };

  documentRef = React.createRef<HTMLElement>();

  componentDidMount() {
    this.load();
  }

  async getWorkerSrc(): Promise<string> {
    if (this.props.workerSrc) {
      return this.props.workerSrc;
    }

    // Resolve worker URL based on document base path
    const baseUrl = document.querySelector("base")?.getAttribute("href") || "/";
    const normalizedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    const workerUrl = `${normalizedBase}assets/pdf.worker.min.mjs`;

    try {
      // Fetch worker file and inject polyfill for older browsers
      const response = await fetch(workerUrl);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch worker: ${response.status} ${response.statusText}`
        );
      }

      const workerContent = await response.text();

      // Prepend polyfill to worker content
      const polyfill = `
// Polyfill for Promise.withResolvers (ES2024 feature)
if (typeof Promise.withResolvers === 'undefined') {
  Promise.withResolvers = function () {
    let resolve, reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

`;
      const combinedWorkerCode = polyfill + workerContent;

      // Create Blob URL with combined content
      const blob = new Blob([combinedWorkerCode], {
        type: "application/javascript",
      });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("[PdfLoader] Failed to create worker blob:", error);
      // Fall back to original worker URL
      return workerUrl;
    }
  }

  componentWillUnmount() {
    const { pdfDocument: discardedDocument } = this.state;
    if (discardedDocument) {
      discardedDocument.destroy();
    }
  }

  componentDidUpdate({ url }: Props) {
    if (this.props.url !== url) {
      this.load();
    }
  }

  componentDidCatch(error: Error) {
    console.error("[PdfLoader] Error:", error);

    const { onError } = this.props;
    if (onError) {
      onError(error);
    }

    this.setState({ pdfDocument: null, error });
  }

  async load() {
    const { ownerDocument = document } = this.documentRef.current || {};
    const { url, cMapUrl, cMapPacked } = this.props;
    const { pdfDocument: discardedDocument } = this.state;

    this.setState({ pdfDocument: null, error: null });

    try {
      // Setup worker with polyfill
      const workerSrc = await this.getWorkerSrc();
      if (workerSrc) {
        GlobalWorkerOptions.workerSrc = workerSrc;
      }

      // Destroy old document if exists
      await discardedDocument?.destroy();

      // Validate URL
      if (!url) {
        return;
      }

      // Load PDF document
      const document = {
        ...this.props,
        ownerDocument,
        cMapUrl,
        cMapPacked,
      };

      const pdfDocument = await getDocument(document).promise;
      this.setState({ pdfDocument });
    } catch (e) {
      this.componentDidCatch(e as Error);
    }
  }

  render() {
    const { children, beforeLoad } = this.props;
    const { pdfDocument, error } = this.state;

    return (
      <>
        <span ref={this.documentRef} />
        {error
          ? this.renderError()
          : !pdfDocument || !children
            ? beforeLoad
            : children(pdfDocument)}
      </>
    );
  }

  renderError() {
    const { errorMessage } = this.props;

    if (errorMessage) {
      return React.cloneElement(errorMessage, { error: this.state.error });
    }

    return null;
  }
}
